// 404 page — fires for any unknown URL (typed sign-in path, stale link,
// any other unmatched route) AND for runtime exceptions thrown inside a
// matched route. React Router's createBrowserRouter uses a single
// errorElement on the root route for both cases.
//
// M6.2-followup-2 (Kaillum smoke test 2026-06-07): hooproster.com/sign-in
// previously rendered the raw React Router dev-style ErrorBoundary —
// internal scaffolding leaking to public visitors. This page replaces
// that with the same look-and-feel as Landing (court-watermark stage,
// HoopRoster wordmark, theme toggle), one line of explanation, and a
// CTA back to the marketing site.
//
// Standalone (no AppShell / TopNav) for two reasons:
//   1) the unknown route may correspond to a logged-out visitor; no
//      need to fetch session/me data on a dead-end.
//   2) the AppShell's outer layout may itself be the source of the
//      thrown error — rendering it on top of the error page risks an
//      infinite recursion into the error boundary. Landing already
//      ships a self-contained layout we can mirror.
//
// useRouteError() differentiates "route not matched" (404, common) from
// "child route threw" (5xx-ish, rare). The copy stays generic — the
// developer-facing distinction is in the console, not the UI.

import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom'
import { CourtBackground } from '../components/CourtBackground'
import ThemeToggle from '../components/ThemeToggle'

export default function NotFound() {
  const error = useRouteError()
  // Three call sites:
  //   1) `path: '*'` catch-all (no error thrown)        → 404
  //   2) errorElement with isRouteErrorResponse 404     → 404
  //   3) errorElement with isRouteErrorResponse 5xx     → generic
  //   4) errorElement with a thrown JS exception (rare) → generic
  // useRouteError() returns undefined for #1, so the no-error case
  // gets the 404 copy explicitly — otherwise the most common path
  // (typed unknown URL) would show "Something went wrong".
  const is404 =
    error == null || (isRouteErrorResponse(error) && error.status === 404)
  return (
    <main className="relative min-h-screen flex items-center justify-center px-6 py-12 bg-hoop-bg">
      <CourtBackground className="absolute inset-0 text-hoop-text opacity-[0.07] dark:opacity-[0.08]" />
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>
      <div className="relative z-10 max-w-md w-full text-center">
        <div className="text-sm uppercase tracking-widest text-hoop-text-3 mb-3">
          HoopRoster
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-hoop-text mb-3">
          {is404 ? 'Page not found' : 'Something went wrong'}
        </h1>
        <p className="text-base text-hoop-text-2 mb-8">
          {is404
            ? "The page you're looking for doesn't exist or was moved."
            : "An error occurred loading this page. Try again, or head back."}
        </p>
        <div className="flex items-center justify-center gap-3">
          <Link
            to="/"
            className="bg-hoop-primary text-hoop-primary-fg px-4 py-2 rounded-md text-sm font-medium hover:opacity-90"
          >
            Back to home
          </Link>
          <Link
            to="/discover"
            className="px-4 py-2 rounded-md text-sm font-medium border border-hoop-border text-hoop-text-2 hover:bg-hoop-bg"
          >
            Browse players
          </Link>
        </div>
      </div>
    </main>
  )
}
